import { useState, useEffect, useRef, lazy, Suspense } from 'react'
import { useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Header } from '@/components/landing/Header'
import { Footer } from '@/components/landing/Footer'
import { NavigationSidebar } from '@/components/docs/installation/NavigationSidebar'
import { navigationItems } from '@/components/docs/installation/constants'
import { useTranslation } from '@/hooks/useTranslation'
import { toAbsoluteUrl } from '@/lib/seo'

// Import base sections directly for initial render (SSR)
import { OverviewSection } from '@/components/docs/installation/sections/OverviewSection'
import { GeneralInstallationSection } from '@/components/docs/installation/sections/GeneralInstallationSection'
import { LanguageConfigurationSection } from '@/components/docs/installation/sections/LanguageConfigurationSection'
import { NextJsSection } from '@/components/docs/installation/sections/NextJsSection'
import { ReactSection } from '@/components/docs/installation/sections/ReactSection'

// Lazy load remaining sections for better performance
const ProgrammaticTriggersSection = lazy(() => 
	import('@/components/docs/installation/sections/ProgrammaticTriggersSection')
		.then(module => ({ default: module.ProgrammaticTriggersSection }))
)
const UserIdentificationSection = lazy(() => 
	import('@/components/docs/installation/sections/UserIdentificationSection')
		.then(module => ({ default: module.UserIdentificationSection }))
)
const WidgetControlApiSection = lazy(() => 
	import('@/components/docs/installation/sections/WidgetControlApiSection')
		.then(module => ({ default: module.WidgetControlApiSection }))
)
const VueJsSection = lazy(() => 
	import('@/components/docs/installation/sections/VueJsSection')
		.then(module => ({ default: module.VueJsSection }))
)
const WordPressSection = lazy(() => 
	import('@/components/docs/installation/sections/WordPressSection')
		.then(module => ({ default: module.WordPressSection }))
)
const LaravelSection = lazy(() => 
	import('@/components/docs/installation/sections/LaravelSection')
		.then(module => ({ default: module.LaravelSection }))
)
const DjangoSection = lazy(() => 
	import('@/components/docs/installation/sections/DjangoSection')
		.then(module => ({ default: module.DjangoSection }))
)
const WebflowSection = lazy(() => 
	import('@/components/docs/installation/sections/WebflowSection')
		.then(module => ({ default: module.WebflowSection }))
)
const FramerSection = lazy(() => 
	import('@/components/docs/installation/sections/FramerSection')
		.then(module => ({ default: module.FramerSection }))
)
const ShopifySection = lazy(() => 
	import('@/components/docs/installation/sections/ShopifySection')
		.then(module => ({ default: module.ShopifySection }))
)
const StaticHtmlSection = lazy(() => 
	import('@/components/docs/installation/sections/StaticHtmlSection')
		.then(module => ({ default: module.StaticHtmlSection }))
)
const TroubleshootingSection = lazy(() => 
	import('@/components/docs/installation/sections/TroubleshootingSection')
		.then(module => ({ default: module.TroubleshootingSection }))
)
const WebhooksSection = lazy(() => 
	import('@/components/docs/installation/sections/WebhooksSection')
		.then(module => ({ default: module.WebhooksSection }))
)

// Loading component for lazy loaded sections
const SectionLoader = () => {
	const { t } = useTranslation()
	return (
		<div className="flex items-center justify-center py-12">
			<div className="animate-pulse text-gray-500">{t('docsInstallation.loadingSection')}</div>
		</div>
	)
}

const DocsInstallation = () => {
	const { t } = useTranslation()
	const [activeSection, setActiveSection] = useState('overview')
	const location = useLocation()
	const pendingHashRef = useRef<string | null>(null)

	// Handle scroll to section and update URL
	const scrollToSection = (sectionId: string) => {
		pendingHashRef.current = null
		setActiveSection(sectionId)
		const element = document.getElementById(sectionId)
		if (element) {
			element.scrollIntoView({ behavior: 'smooth', block: 'start' })
		}
		// Update URL hash for shareable links
		window.history.replaceState(null, '', `#${sectionId}`)
	}

	// Wait for lazy-loaded sections before attempting hash navigation.
	useEffect(() => {
		const hash = location.hash.replace('#', '')
		const hasValidHash = navigationItems.some((item) => item.id === hash)

		if (!hash || !hasValidHash) {
			pendingHashRef.current = null
			window.scrollTo(0, 0)
			return
		}

		pendingHashRef.current = hash

		let isCancelled = false
		let attemptCount = 0
		const maxAttempts = 30

		const scrollToHash = () => {
			if (isCancelled) {
				return
			}

			const element = document.getElementById(hash)
			if (element) {
				setActiveSection(hash)
				pendingHashRef.current = null
				element.scrollIntoView({ behavior: 'smooth', block: 'start' })
				return
			}

			if (attemptCount < maxAttempts) {
				attemptCount += 1
				window.setTimeout(scrollToHash, 100)
			}
		}

		scrollToHash()

		return () => {
			isCancelled = true
		}
	}, [location.hash, location.pathname])

	// Handle scroll tracking once hash navigation has settled.
	useEffect(() => {
		const handleScroll = () => {
			if (pendingHashRef.current) {
				return
			}

			const sectionElements = navigationItems.map((item) =>
				document.getElementById(item.id)
			)
			let current = 'overview' // Default to overview

			// Find the section that's currently most visible
			const scrollPosition = window.scrollY + 100 // Add offset for better UX

			for (let i = sectionElements.length - 1; i >= 0; i--) {
				const section = sectionElements[i]
				if (section) {
					const rect = section.getBoundingClientRect()
					const elementTop = rect.top + window.scrollY

					// Check if we've scrolled past this section
					if (scrollPosition >= elementTop) {
						current = section.id
						break
					}
				}
			}
			setActiveSection(current)
			// Update URL hash when scrolling manually
			if (current !== location.hash.replace('#', '')) {
				window.history.replaceState(
					null,
					'',
					current === 'overview' ? location.pathname : `#${current}`
				)
			}
		}

		// Set initial state
		handleScroll()

		// Use throttling for better performance
		let ticking = false
		const throttledHandleScroll = () => {
			if (!ticking) {
				requestAnimationFrame(() => {
					handleScroll()
					ticking = false
				})
				ticking = true
			}
		}

		window.addEventListener('scroll', throttledHandleScroll)
		return () => window.removeEventListener('scroll', throttledHandleScroll)
	}, [location.hash, location.pathname])

	return (
		<div className="flex min-h-screen w-full flex-col bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900">
			<Helmet>
				<title>{t('docsInstallation.seoTitle')}</title>
				<meta name="description" content={t('docsInstallation.seoDescription')} />
				<link rel="canonical" href={toAbsoluteUrl('/docs')} />
			</Helmet>
			<Header />
			<div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex">
				{/* Sidebar Navigation */}
				<NavigationSidebar 
					activeSection={activeSection} 
					onSectionClick={scrollToSection} 
				/>

				{/* Main Content */}
				<main className="flex-1 min-w-0">
					<div className="px-4 py-12 sm:px-6 lg:px-8">
						{/* Core sections - Always render for SSR */}
						<OverviewSection />
						<GeneralInstallationSection />
						<LanguageConfigurationSection />
						
						{/* Lazy load sections that are less commonly accessed */}
						<Suspense fallback={<SectionLoader />}>
							<ProgrammaticTriggersSection />
						</Suspense>
						
						<Suspense fallback={<SectionLoader />}>
							<UserIdentificationSection />
						</Suspense>
						
						<Suspense fallback={<SectionLoader />}>
							<WidgetControlApiSection />
						</Suspense>
						
						{/* Framework-specific sections */}
						<NextJsSection />
						<ReactSection />
						
						<Suspense fallback={<SectionLoader />}>
							<VueJsSection />
						</Suspense>
						
						<Suspense fallback={<SectionLoader />}>
							<WordPressSection />
						</Suspense>
						
						<Suspense fallback={<SectionLoader />}>
							<LaravelSection />
						</Suspense>
						
						<Suspense fallback={<SectionLoader />}>
							<DjangoSection />
						</Suspense>
						
						<Suspense fallback={<SectionLoader />}>
							<WebflowSection />
						</Suspense>
						
						<Suspense fallback={<SectionLoader />}>
							<FramerSection />
						</Suspense>
						
						<Suspense fallback={<SectionLoader />}>
							<ShopifySection />
						</Suspense>
						
						<Suspense fallback={<SectionLoader />}>
							<StaticHtmlSection />
						</Suspense>
						
						<Suspense fallback={<SectionLoader />}>
							<WebhooksSection />
						</Suspense>
						
						<Suspense fallback={<SectionLoader />}>
							<TroubleshootingSection />
						</Suspense>
					</div>
				</main>
			</div>
			<Footer />
		</div>
	)
}

export default DocsInstallation
